import _ from 'underscore';
import PropTypes from 'prop-types';
import React from 'react';
import { PremiumIcon } from 'javascripts/analytics/components/premium_icon';
import { TableHeadSortingCell } from 'javascripts/analytics/components/table_head_sorting_cell';
import { inMode } from 'javascripts/analytics/config/permissions';
import { FindReadersButton } from 'js/find_readers/FindReadersButton';
import { pageOffersFindReaders } from 'js/find_readers/eligibility';
import { NavLink } from 'react-router-dom';
import {
  trackAnalyticsClick,
  watchAnalyticsImpression,
} from 'js/analytics/metrics/analyticsTracking';

// Instrumentation for the Find Readers CTA in the last column. Redshift stores
// the source in a varchar(64) and COPY runs TRUNCATECOLUMNS, so an over-long
// name would silently merge with its neighbours.
const FIND_READERS_SOURCE = 'analytics-papers-find-readers';
const FIND_READERS_UPGRADE_TRIGGER = 'analytics_papers_find_readers';

export class DocumentsTabTable extends React.Component {
  static propTypes = {
    docs: PropTypes.array.isRequired,
    dayCount: PropTypes.number.isRequired,
  };

  state = {
    sortBy: 'thirtyDayViews',
    sortDirection: 'DESC',
  };

  impressionRefs = new Map();

  componentWillUnmount() {
    this.impressionRefs.forEach((ref) => ref(null));
  }

  sortedDocs = () => {
    const docs = _.sortBy(
      this.props.docs,
      function (doc) {
        return doc[this.state.sortBy];
      },
      this,
    );

    if (this.state.sortDirection === 'ASC') return docs;

    return _.clone(docs).reverse();
  };

  updateSort = (sortBy) => {
    return function () {
      trackAnalyticsClick('analytics-sort', {
        table: 'papers',
        column: sortBy,
      });
      this.setState({
        sortBy: sortBy,
        sortDirection: this.state.sortDirection === 'ASC' ? 'DESC' : 'ASC',
      });
    }.bind(this);
  };

  upgradeDoorImpressionRef = (elementKey, door, trigger) => {
    if (!this.impressionRefs.has(elementKey)) {
      let currentElement = null;
      let watcher = null;
      this.impressionRefs.set(elementKey, (element) => {
        if (element === currentElement) return;
        watcher?.stop();
        watcher = null;
        currentElement = element;
        if (element) {
          watcher = watchAnalyticsImpression(
            element,
            'analytics-upgrade-door',
            door,
            { door, trigger },
          );
        }
      });
    }
    return this.impressionRefs.get(elementKey);
  };

  trackUpgradeDoorClick = (door, trigger, entityId) => {
    trackAnalyticsClick(
      'analytics-upgrade-door',
      { door, trigger },
      Number.isInteger(entityId) ? entityId : undefined,
    );
  };

  perDocumentPages = () => {
    return inMode('premium');
  };

  upgradeableForPerDocument = () => {
    return inMode('upgradeable');
  };

  shouldShowImpactColumn = () => {
    return this.perDocumentPages() || this.upgradeableForPerDocument();
  };

  // The whole VIEWER-level decision for the Find Readers column, already made
  // by AnalyticsDashboardController and published as a body class — the same
  // signal the buttons themselves wait for. Deliberately NOT an inMode() check:
  // the audience here is not "premium vs upgradeable" but
  // FindReaders::Eligibility's, under which a free author sees the column as an
  // upsell while a premium author who has never been through the pitch sees no
  // column at all. Restated in JS it would be a second, driftable copy.
  //
  // Per-paper eligibility is a separate question each button asks for itself,
  // so a shown column can still be empty of buttons.
  shouldShowFindReadersColumn = () => {
    return pageOffersFindReaders();
  };

  renderDocRows = () => {
    return _.map(
      this.sortedDocs(),
      function (doc) {
        const impactLink = this.props.match.url + '/' + doc.id;

        return (
          // Keyed by the work, not the row position: sorting reorders this list
          // in place, and under an index key React would hand a mounted
          // FindReadersButton a different paper while keeping its resolved
          // eligibility.
          <tr key={doc.id}>
            <td>
              {inMode('premium') ? (
                <NavLink
                  to={impactLink}
                  onClick={() =>
                    trackAnalyticsClick(
                      'analytics-content-link',
                      { link_type: 'paper', table: 'papers' },
                      Number.isInteger(doc.id) ? doc.id : undefined,
                    )
                  }
                >
                  {doc.title}
                </NavLink>
              ) : (
                <a
                  href={doc.internal_url}
                  onClick={() =>
                    trackAnalyticsClick(
                      'analytics-content-link',
                      { link_type: 'paper', table: 'papers' },
                      Number.isInteger(doc.id) ? doc.id : undefined,
                    )
                  }
                >
                  {doc.title}
                </a>
              )}
            </td>
            <td className="u-fontMonospaced u-fs12">
              {$h.commaizeInt(doc.thirtyDayViews)}
            </td>
            <td className="u-fontMonospaced u-fs12">
              {$h.commaizeInt(doc.thirtyDayUniques)}
            </td>
            <td className="u-fontMonospaced u-fs12">
              {$h.commaizeInt(doc.thirtyDayDownloads)}
            </td>
            <td className="u-fontMonospaced u-fs12">
              {$h.commaizeInt(doc.allTimeViews)}
            </td>
            <td className="u-fontMonospaced u-fs12">
              {$h.commaizeInt(doc.allTimeDownloads)}
            </td>
            {this.shouldShowImpactColumn() && (
              <td className="u-fontMonospaced u-fs12 u-taCenter u-verticalAlignMiddle">
                {inMode('premium') ? (
                  <NavLink
                    className="Button Button--sm Button--inverseBlue u-ph3x"
                    to={impactLink}
                  >
                    Impact
                  </NavLink>
                ) : (
                  <a
                    className="Button Button--sm Button--inverseBlue u-ph3x"
                    ref={this.upgradeDoorImpressionRef(
                      `papers_impact_cell:${doc.id}`,
                      'papers_impact_cell',
                      'documents-table-impact-cell',
                    )}
                    href={
                      inMode('upgradeable') &&
                      $r.upgrade_url({
                        feature: 'academia_analytics_expanded',
                        trigger: 'documents-table-impact-cell',
                      })
                    }
                    onClick={() =>
                      this.trackUpgradeDoorClick(
                        'papers_impact_cell',
                        'documents-table-impact-cell',
                        doc.id,
                      )
                    }
                  >
                    Impact
                  </a>
                )}
              </td>
            )}
            {this.shouldShowFindReadersColumn() && (
              <td className="u-taCenter u-verticalAlignMiddle">
                <FindReadersButton
                  workId={doc.id}
                  source={FIND_READERS_SOURCE}
                  upgradeTrigger={FIND_READERS_UPGRADE_TRIGGER}
                />
              </td>
            )}
          </tr>
        );
      }.bind(this),
    );
  };

  renderHeadCell = (column, style, text) => {
    return (
      <TableHeadSortingCell
        sortingByColumn={this.state.sortBy === column}
        sortDirection={this.state.sortDirection}
        onClick={this.updateSort(column)}
        style={style}
      >
        {text}
      </TableHeadSortingCell>
    );
  };

  renderImpactColumnHeadContentsWrapper = (children) => {
    const upgradeUrl = $r.upgrade_url({
      feature: 'analytics',
      trigger: 'documents-table-impact-header-cell',
    });

    if (inMode('premium')) {
      return (
        <span className="js-PremiumIconContainerHoverWrapper PremiumIconContainerHoverWrapper">
          {children}
        </span>
      );
    } else {
      return (
        <a
          className="js-PremiumIconContainerHoverWrapper PremiumIconContainerHoverWrapper u-linkUnstyled u-textDecorationNone"
          ref={this.upgradeDoorImpressionRef(
            'papers_impact_header',
            'papers_impact_header',
            'documents-table-impact-header-cell',
          )}
          href={upgradeUrl}
        >
          {children}
        </a>
      );
    }
  };

  renderImpactColumnHead = () => {
    const upgradeUrl = $r.upgrade_url({
      feature: 'academia_analytics_expanded',
      trigger: 'documents-table-impact-header-cell',
    });
    return (
      <th className="u-taCenter u-fw400" style={{ width: '116px' }}>
        {this.renderImpactColumnHeadContentsWrapper(
          <span>
            <PremiumIcon
              style={{ top: '-1px' }}
              className="u-positionRelative"
              popoverBodyText="Get detailed impact reports to show the impact of each paper."
              popoverLinkUrl={upgradeUrl}
              disableHoverBehavior={inMode('premium')}
              onTriggerClick={() =>
                this.trackUpgradeDoorClick(
                  'papers_impact_header',
                  'documents-table-impact-header-cell',
                )
              }
              onPopoverLinkClick={() =>
                this.trackUpgradeDoorClick(
                  'papers_impact_header',
                  'documents-table-impact-header-cell',
                )
              }
            />
            <span className="u-fw700">&nbsp; Impact</span>
          </span>,
        )}
      </th>
    );
  };

  // Mirrors renderImpactColumnHeadContentsWrapper: premium members get a plain
  // span, everyone else gets the whole header as a link to the pitch, so the
  // affordance does not depend on finding the small button inside the popover.
  renderFindReadersColumnHeadContentsWrapper = (children) => {
    if (inMode('premium')) {
      return (
        <span className="js-PremiumIconContainerHoverWrapper PremiumIconContainerHoverWrapper">
          {children}
        </span>
      );
    }

    return (
      // eslint-disable-next-line no-restricted-syntax -- Byte-for-byte the Impact header's wrapper above, which a DS link would restyle; the two sit side by side in one row and must read as one treatment.
      <a
        className="js-PremiumIconContainerHoverWrapper PremiumIconContainerHoverWrapper u-linkUnstyled u-textDecorationNone"
        ref={this.upgradeDoorImpressionRef(
          'papers_find_readers_header',
          'papers_find_readers_header',
          'documents-table-find-readers-header-cell',
        )}
        href={this.findReadersLandingPageUrl()}
      >
        {children}
      </a>
    );
  };

  // PremiumUpgradeController::FEATURES maps `find_readers` to the FIND_READERS
  // branch, which renders the premium_upgrade/find_readers landing page. That
  // page is also the only way into
  // FindReaders::LandingPageVisitors — the set a premium member must be in
  // before they are offered the button anywhere — so this link is load-bearing,
  // not just marketing.
  findReadersLandingPageUrl = () => {
    return $r.upgrade_url({
      feature: 'find_readers',
      trigger: 'documents-table-find-readers-header-cell',
    });
  };

  renderFindReadersColumnHead = () => {
    return (
      <th className="u-taCenter u-fw400" style={{ width: '150px' }}>
        {this.renderFindReadersColumnHeadContentsWrapper(
          <span>
            <PremiumIcon
              style={{ top: '-1px' }}
              className="u-positionRelative"
              popoverBodyText="Find academics whose current research is similar, and invite them to read your paper."
              popoverLinkUrl={this.findReadersLandingPageUrl()}
              disableHoverBehavior={inMode('premium')}
              onTriggerClick={() =>
                this.trackUpgradeDoorClick(
                  'papers_find_readers_header',
                  'documents-table-find-readers-header-cell',
                )
              }
              onPopoverLinkClick={() =>
                this.trackUpgradeDoorClick(
                  'papers_find_readers_header',
                  'documents-table-find-readers-header-cell',
                )
              }
            />
            <span className="u-fw700">&nbsp; Find Readers</span>
          </span>,
        )}
      </th>
    );
  };

  render() {
    return (
      <div className="panel panel-flat  a-fadeInDown">
        <div className="panel-body" style={{ overflowX: 'auto' }}>
          <table
            className="analytics-dashboard-data-table table table-bordered table-hover u-mb0x"
            style={{ tableLayout: 'auto' }}
          >
            <thead>
              <tr>
                <th style={{ minWidth: '300px' }}>Title</th>
                {this.renderHeadCell(
                  'thirtyDayViews',
                  { width: '11%' },
                  this.props.dayCount + '-day views',
                )}
                {this.renderHeadCell(
                  'thirtyDayUniques',
                  { width: '12.5%' },
                  this.props.dayCount + '-day uniques',
                )}
                {this.renderHeadCell(
                  'thirtyDayDownloads',
                  { width: '14%' },
                  this.props.dayCount + '-day downloads',
                )}
                {this.renderHeadCell(
                  'allTimeViews',
                  { width: '11.5%' },
                  'All-time views',
                )}
                {this.renderHeadCell(
                  'allTimeDownloads',
                  { width: '14.5%' },
                  'All-time downloads',
                )}
                {this.shouldShowImpactColumn() && this.renderImpactColumnHead()}
                {this.shouldShowFindReadersColumn() &&
                  this.renderFindReadersColumnHead()}
              </tr>
            </thead>
            <tbody>{this.renderDocRows()}</tbody>
          </table>
        </div>
      </div>
    );
  }
}
